/* eslint-disable no-console */
// eslint-disable-next-line max-classes-per-file
import { EventEmitter, Injectable, NgZone, inject } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import { AuthenticationResult } from "@azure/msal-browser";
import {
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    IRetryPolicy,
    RetryContext
} from "@microsoft/signalr";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import { CompressService } from "~app/common/services/compress.service";
import { environment } from "~environments/environment";
import { ObjectUtils } from "../utils/object-utils";
import { EventService, IEvent } from "./cqs/event.service";

export type ConnectionStatus = {
    lastError?: Error;
    state: HubConnectionState;
};

class EventhubSingeltonService {
    connectionStatusSrc = new BehaviorSubject<ConnectionStatus>({
        state: HubConnectionState.Disconnected
    });

    private readonly retryPolicy: IRetryPolicy = {
        nextRetryDelayInMilliseconds: (retryContext: RetryContext) => retryContext.elapsedMilliseconds < 60000 ? 3000 : 60000
    };

    // eslint-disable-next-line @typescript-eslint/no-empty-object-type
    private readonly subscriptions: {} = {};
    private readonly subscriptionsCounter: Map<string, number> = new Map<string, number>();

    private eventService: EventService;
    private hubConnection: HubConnection;
    private initPromise: Promise<any>;
    private track: boolean;

    private static instance: EventhubSingeltonService;

    constructor(private readonly ngZone: NgZone, private readonly msalAuthService: MsalService) {
    }

    static getInstance(eventService: EventService, ngZone: NgZone, msalAuthService: MsalService): EventhubSingeltonService {
        if (!EventhubSingeltonService.instance) {
            EventhubSingeltonService.instance = new EventhubSingeltonService(ngZone, msalAuthService);
            EventhubSingeltonService.instance.eventService = eventService;
        }

        return EventhubSingeltonService.instance;
    }

    onHubReconnect() {
        console.log("SignalR reconnected");
        this.connectionStatusSrc.next({
            state: this.hubConnection.state,
            lastError: null
        });
        // Resubscribe on topics when reconnected this.subscriptions
        const previousSubscriptions = JSON.parse(JSON.stringify(this.subscriptions));
        for (const prop in previousSubscriptions) {
            if (Object.hasOwn(previousSubscriptions, prop) && previousSubscriptions[prop]) {
                this.invokeListener(prop, true, this.track);
            }
        }
    }

    async registerListener(topic: string, track = false) {
        this.track = track;
        if (this.subscriptionsCounter.has(topic)) {
            let count = this.subscriptionsCounter.get(topic);
            count++;
            this.subscriptionsCounter.set(topic, count);
            return;
        } else {
            this.subscriptionsCounter.set(topic, 1);
        }

        if (!this.initPromise) {
            this.initPromise = new Promise(((resolve, reject) => {
                this.ngZone.runOutsideAngular(async () => {
                    console.log("SignalR Hub connection starting...");
                    const options = {
                        accessTokenFactory: async () => {
                            const auth: AuthenticationResult = await firstValueFrom(this.msalAuthService.acquireTokenSilent({ scopes: environment.apiConfig.scopes }));
                            const compressAccessToken = await CompressService.CompressAccessToken(auth.accessToken);
                            return compressAccessToken;
                        },
                        headers: { "x-encrypted-at": "true" }
                    };
                    this.hubConnection = new HubConnectionBuilder()
                        .withUrl(`${environment.apiV2}/events`, options)
                        .withAutomaticReconnect(this.retryPolicy)
                        .build();

                    this.hubConnection.onclose((error) => this.connectionStatusSrc.next({
                        state: this.hubConnection.state,
                        lastError: error
                    }));
                    this.hubConnection.on("onevent", (message: any) => this.messageReceived(message));

                    this.hubConnection.onreconnected(() => this.onHubReconnect());
                    this.hubConnection.onreconnecting((error: Error) => {
                        this.connectionStatusSrc.next({
                            state: this.hubConnection.state,
                            lastError: error
                        });
                        console.log("SignalR reconnecting...");
                    });
                    this.hubConnection.start()
                        .then(() => {
                            console.log("SignalR Hub connection started");
                            this.connectionStatusSrc.next({
                                state: this.hubConnection.state
                            });
                            resolve(true);
                        })
                        .catch(err => {
                            reject(err);
                            this.connectionStatusSrc.next({
                                state: this.hubConnection.state,
                                lastError: err
                            });
                            console.log("SignalR Error while establishing connection");
                        });
                });
            }));
        }
        await this.initPromise;
        this.invokeListener(topic, true, track);
    }

    async unregisterListener(topic: string, track: boolean) {
        await this.ngZone.runOutsideAngular(async () => {
            if (this.subscriptionsCounter.has(topic)) {
                let count = this.subscriptionsCounter.get(topic);
                count--;
                this.subscriptionsCounter.set(topic, count);
                if (count === 0) {
                    await this.initPromise;
                    this.invokeListener(topic, false, track);
                    this.subscriptionsCounter.delete(topic);
                }
            }
        });
    }

    private invokeListener(topic: string, register: boolean, track: boolean) {
        // eslint-disable-next-line sonarjs/no-selector-parameter
        if (register) {
            this.listener(topic, true, track);
            this.subscriptions[topic] = true;
        } else {
            this.listener(topic, false, track);
            delete this.subscriptions[topic];
        }
    }

    private listener(topic: string, register: boolean, track: boolean) {
        this.ngZone.runOutsideAngular(x => {

            if (register) {
                console.log(`SignalR starts listening on topic ${topic}`, this.hubConnection.connectionId);
                this.hubConnection.invoke("subscribetrackable", topic, track);
            } else {
                console.log(`SignalR stops listening on topic ${topic}`, this.hubConnection.connectionId);
                this.hubConnection.invoke("unsubscribetrackable", topic, track);
            }
        });
    }

    private messageReceived(message: any) {
        console.log(`SignalR Received event-message: ${message}`);
        const parsed = JSON.parse(message);
        this.ngZone.run(() => {
            this.eventService.emit(ObjectUtils.toCamel(parsed) as IEvent);
        });
    }
}

@Injectable({
    providedIn: "root"
})
export class EventHubService {
    onMessageReceived = new EventEmitter<string>();

    protected eventhubSingeltonService: EventhubSingeltonService;

    private readonly eventService = inject(EventService);

    constructor() {
        const eventService = this.eventService;
        const ngZone = inject(NgZone);
        const msalAuthService = inject(MsalService);

        this.eventhubSingeltonService = EventhubSingeltonService.getInstance(eventService, ngZone, msalAuthService);
    }

    get connectionStatus$() {
        return this.eventhubSingeltonService.connectionStatusSrc.asObservable();
    }

    async registerListener(topic: string, track = false) {
        await this.eventhubSingeltonService.registerListener(topic, track);
    }

    async unregisterListener(topic: string, track = false) {
        await this.eventhubSingeltonService.unregisterListener(topic, track);
    }
}
